/**
 * A short two-note ping for new questions, synthesised with the Web Audio API
 * so there is no asset to ship, preload or 404 on.
 *
 * Browsers create an AudioContext suspended until the page has seen a user
 * gesture, so `primeChime` is wired to the first interaction on the page and
 * `playChime` resumes defensively in case that gesture never came.
 */
let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  return ctx;
}

export function primeChime(): void {
  const c = getCtx();
  if (c && c.state === 'suspended') void c.resume().catch(() => {});
}

/** Two soft sine notes, ~0.4s total. Quiet enough not to carry past the podium. */
export function playChime(): void {
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') void c.resume().catch(() => {});

  const notes = [
    { freq: 880, at: 0 },      // A5
    { freq: 1174.66, at: 0.09 }, // D6
  ];

  for (const { freq, at } of notes) {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;

    // exponentialRamp cannot touch zero, so fade between tiny non-zero values.
    const t = c.currentTime + at;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.1, t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);

    osc.connect(gain).connect(c.destination);
    osc.start(t);
    osc.stop(t + 0.3);
  }
}
